import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, FileText, Search, Download, Plus, Pencil, LinkIcon } from "lucide-react";
import api, { fileUrl } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/AppLayout";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { FileUploader, FileChip } from "../components/module-widgets";

const CATEGORIES = ["Policy", "RAMS", "Risk Assessment", "Method Statement", "SSOW", "Emergency Plan",
  "Toolbox Talk", "Form", "Procedure", "Video", "Training Guide", "Other"];

export default function Resources() {
  const { user } = useAuth();
  const canEdit = user?.role === "super_admin";
  const [res, setRes] = useState(null);
  const [q, setQ] = useState("");
  const [dlg, setDlg] = useState(null);
  const [form, setForm] = useState(null);

  const load = () => api.get("/resources").then((r) => setRes(r.data));
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm({ name: "", category: "Policy", version: "v1.0", source: "file", link: "", file: null }); setDlg("new"); };
  const openEdit = (e, r) => { e.stopPropagation(); setForm({ ...r, source: r.source || (r.file ? "file" : "link") }); setDlg("edit"); };

  const save = async () => {
    if (!form.name) return toast.error("Name is required");
    if (form.source === "link" && !form.link) return toast.error("Please add a link");
    if (form.source === "file" && !form.file) return toast.error("Please upload a file");
    try {
      const payload = { name: form.name, category: form.category, version: form.version,
        source: form.source, link: form.source === "link" ? form.link : null,
        file: form.source === "file" ? form.file : null,
        file_type: form.source === "file" ? (form.file?.filename?.split(".").pop()?.toUpperCase() || "PDF") : "Link" };
      if (dlg === "edit") await api.patch(`/resources/${form.id}`, payload);
      else await api.post("/resources", payload);
      toast.success(dlg === "edit" ? "Resource updated" : "Resource added");
      setDlg(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save"); }
  };

  const openResource = (r) => {
    if (r.source === "link" && r.link) window.open(r.link, "_blank");
    else if (r.file?.path) window.open(fileUrl(r.file.path), "_blank");
    else toast.info("No file or link attached to this resource.");
  };

  if (!res) return <div className="flex h-64 items-center justify-center"><Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--accent))]" /></div>;
  const filtered = res.filter((r) => !q || r.name.toLowerCase().includes(q.toLowerCase()) || r.category.toLowerCase().includes(q.toLowerCase()));

  return (
    <div>
      <PageHeader title="Resource Library" subtitle="Policies, RAMS, forms and guides — upload a PDF or add a link.">
        {canEdit && <Button className="rounded-full" onClick={openNew} data-testid="add-resource-btn"><Plus className="mr-2 h-4 w-4" /> Add Resource</Button>}
      </PageHeader>

      <div className="relative mb-4 max-w-md">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search resources…" className="pl-9" data-testid="resources-search" />
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {filtered.map((r) => (
          <Card key={r.id} onClick={() => openResource(r)} data-testid={`resource-${r.id}`}
            className="group flex cursor-pointer items-start gap-3 rounded-2xl border-border p-5 transition-all hover:-translate-y-1 hover:shadow-md">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--accent))]/10 text-[hsl(var(--accent))]">
              {r.source === "link" ? <LinkIcon className="h-5 w-5" /> : <FileText className="h-5 w-5" />}
            </div>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium">{r.name}</div>
              <div className="mt-0.5 flex items-center gap-2 text-xs text-muted-foreground">
                <Badge variant="secondary" className="rounded-full text-[10px]">{r.category}</Badge>
                <span className="font-mono">{r.version}</span>
              </div>
            </div>
            <div className="flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
              {canEdit && <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full" onClick={(e) => openEdit(e, r)} data-testid={`edit-resource-${r.id}`}><Pencil className="h-4 w-4" /></Button>}
              <Button size="icon" variant="ghost" className="h-8 w-8 rounded-full"><Download className="h-4 w-4" /></Button>
            </div>
          </Card>
        ))}
      </div>
      {filtered.length === 0 && <div className="py-12 text-center text-sm text-muted-foreground">No resources found.</div>}

      <Dialog open={!!dlg} onOpenChange={(o) => !o && setDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading">{dlg === "edit" ? "Edit Resource" : "Add Resource"}</DialogTitle></DialogHeader>
          {form && (
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="resource-name" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5"><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}><SelectTrigger data-testid="resource-category"><SelectValue /></SelectTrigger>
                    <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent></Select>
                </div>
                <div className="space-y-1.5"><Label>Version</Label><Input value={form.version} onChange={(e) => setForm({ ...form, version: e.target.value })} data-testid="resource-version" /></div>
              </div>
              <div className="space-y-2">
                <Label>Type</Label>
                <Tabs value={form.source} onValueChange={(v) => setForm({ ...form, source: v })}>
                  <TabsList className="grid w-full grid-cols-2 rounded-full">
                    <TabsTrigger value="file" className="rounded-full" data-testid="resource-type-file">PDF / File Upload</TabsTrigger>
                    <TabsTrigger value="link" className="rounded-full" data-testid="resource-type-link">Link / URL</TabsTrigger>
                  </TabsList>
                </Tabs>
              </div>
              {form.source === "file" ? (
                <div className="space-y-2">
                  {form.file && <FileChip file={form.file} />}
                  <div><FileUploader label={form.file ? "Replace file" : "Upload PDF / document"} testid="resource-upload" onUploaded={(f) => setForm({ ...form, file: f })} /></div>
                </div>
              ) : (
                <div className="space-y-1.5"><Label>URL</Label><Input value={form.link || ""} onChange={(e) => setForm({ ...form, link: e.target.value })} placeholder="https://…" data-testid="resource-link" /></div>
              )}
            </div>
          )}
          <DialogFooter><Button onClick={save} className="rounded-full" data-testid="resource-save-btn">{dlg === "edit" ? "Save Changes" : "Add Resource"}</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
